<template>
  <div class="eb-root">
    <FiPageRail title="ERM Board" :chip="contextTenantName">
      <template #actions>
        <!-- same form as the Event Log's, from the same component -->
        <b-button size="sm" variant="primary" title="Add a new event"
                  aria-label="Add entry" @click="$refs.addEntryModal.open()">
          <i class="fa fa-plus"></i>
        </b-button>
        <erm-board-help />
        <b-button size="sm" variant="outline-secondary" :disabled="loading"
                  title="Reload the board" aria-label="Refresh" @click="reload()">
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
        </b-button>
      </template>
    </FiPageRail>

    <!-- a new event lands in the New lane, so the board reloads on create -->
    <od-add-entry-modal
      ref="addEntryModal"
      modal-id="eb-add-entry-modal"
      :tenant="tenant"
      :sos-enabled="sosEnabled"
      @created="reload"
      @open-event="openEventById"
    />

    <div v-if="loading && !events.length && !error" class="text-center" style="margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-center text-danger" style="margin: 50px;">
      <p>{{ error }}</p>
      <b-button size="sm" variant="outline-secondary" @click="reload()">Retry</b-button>
    </div>

    <div v-else :class="{ 'eb-stale': loading, 'eb-filters-collapsed': !filtersExpanded }">
      <!-- phone-only filters toggle, mirroring the Event Log: everything but
           the search pill folds away so a phone opens straight to the lanes.
           the badge counts narrowed dimensions, search excluded — it has its
           own always-visible field. display:none above 600px. -->
      <button
        type="button"
        class="eb-filter-toggle eb-mobile-only"
        :aria-expanded="filtersExpanded ? 'true' : 'false'"
        @click="filtersExpanded = !filtersExpanded"
      >
        <i class="fa fa-sliders" aria-hidden="true"></i>
        <span>Filters</span>
        <span v-if="activeFilterCount" class="eb-filter-badge">{{ activeFilterCount }}</span>
        <i class="fa" :class="filtersExpanded ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
      </button>

      <div class="eb-scope-row">
        <button type="button" :class="['eb-chip', { 'eb-chip--active': assigneeFilter === MINE }]"
                :aria-pressed="assigneeFilter === MINE ? 'true' : 'false'"
                @click="assigneeFilter = MINE">Mine ({{ myOpenCount }})</button>
        <button type="button" :class="['eb-chip', { 'eb-chip--active': assigneeFilter === '' }]"
                :aria-pressed="assigneeFilter === '' ? 'true' : 'false'"
                @click="assigneeFilter = ''">Everyone</button>
        <button type="button" :class="['eb-chip', { 'eb-chip--active': assigneeFilter === UNASSIGNED }]"
                :aria-pressed="assigneeFilter === UNASSIGNED ? 'true' : 'false'"
                @click="assigneeFilter = UNASSIGNED">Unassigned ({{ unassignedOpenCount }})</button>
        <b-form-select v-model="assigneeDropdown" :options="assigneeOptions" size="sm"
                       class="eb-scope-select" aria-label="Filter by assignee" />
        <!-- narrows the lanes as you type, over the events already loaded.
             the drag hint that used to sit here is gone: the lane notes now
             say what a drop will do while the card is actually in flight. -->
        <div class="eb-search ml-auto">
          <i class="fa fa-search eb-search-icon" aria-hidden="true"></i>
          <input v-model="search" type="text" class="eb-search-input"
                 placeholder="Search events…" aria-label="Search events">
          <button v-if="search" type="button" class="eb-search-clear"
                  aria-label="Clear search" title="Clear search" @click="search = ''">
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <!-- second row: what the cards are, rather than whose they are. same
           Type and Severity vocabulary as the Event Log's chips, so the two
           surfaces filter by the same words. no Status group — the lanes ARE
           the status. -->
      <div class="eb-filter-row">
        <span class="eb-group" role="group" aria-label="Type">
          <span class="eb-group-label">Type</span>
          <button v-for="opt in TYPE_FILTERS" :key="'t-' + opt.value" type="button"
                  :class="['eb-chip', { 'eb-chip--active': typeFilters.includes(opt.value) }]"
                  :aria-pressed="typeFilters.includes(opt.value) ? 'true' : 'false'"
                  @click="toggleFilter('typeFilters', opt.value)">{{ opt.text }}</button>
        </span>
        <span class="eb-group" role="group" aria-label="Severity">
          <span class="eb-group-label">Severity</span>
          <button v-for="opt in SEVERITY_FILTERS" :key="'sv-' + opt.value" type="button"
                  :class="['eb-chip', { 'eb-chip--active': severityFilters.includes(opt.value) }]"
                  :aria-pressed="severityFilters.includes(opt.value) ? 'true' : 'false'"
                  @click="toggleFilter('severityFilters', opt.value)">{{ opt.text }}</button>
        </span>
        <!-- driver / vehicle options come from the rows that survive every
             OTHER filter, so the lists only offer values that will return
             something — and picking one does not collapse its own list. -->
        <b-form-select v-model="driverFilter" :options="driverOptions" size="sm"
                       class="eb-filter-select" aria-label="Filter by driver" />
        <b-form-select v-model="vehicleFilter" :options="vehicleOptions" size="sm"
                       class="eb-filter-select" aria-label="Filter by vehicle" />
        <!-- windows the Resolved lane only, so it sits apart from the filters
             that narrow every lane — pushed to the far right of the row, and
             labelled with the lane it governs. the open lanes are never
             date-bounded (old open work is the point). -->
        <span class="eb-group eb-group--end" role="group" aria-label="Resolved window">
          <span class="eb-group-label">Resolved</span>
          <button v-for="d in RESOLVED_WINDOWS" :key="'rw-' + d" type="button"
                  :class="['eb-chip', { 'eb-chip--active': resolvedWindow === d }]"
                  :aria-pressed="resolvedWindow === d ? 'true' : 'false'"
                  @click="resolvedWindow = d">{{ d }}d</button>
        </span>
      </div>

      <!-- the lanes are capped, so "nothing here" is not the same as "nothing
           exists" — offer the way back and, when a search is what emptied the
           board, the surface that searches the whole history. -->
      <div v-if="isFiltered && !matchCount" class="eb-no-matches">
        No loaded events match these filters.
        <button type="button" class="eb-link-btn" @click="clearFilters">Clear filters</button>
        <template v-if="search.trim()">
          or
          <router-link :to="{ name: 'ops_desk', query: { tenant: tenant, search: search.trim() } }">
            search the full history in the Event Log
          </router-link>
        </template>
      </div>

      <div class="eb-board">
        <div v-for="col in columnDefs" :key="col.status"
             class="eb-col" :class="['eb-col--' + col.status, dropStateClass(col.status)]">
          <div class="eb-col-head">
            <span class="eb-status-chip" :class="'eb-status-chip--' + col.status">
              <i class="fa" :class="statusIcon(col.status)"></i>
              {{ statusLabel(col.status) }}
            </span>
            <span class="eb-col-count">{{ columns[col.status].length }}</span>
          </div>
          <!-- while a card is in flight the note stops describing the lane and
               starts answering "what happens if I let go here?" -->
          <div class="eb-col-note" :class="noteStateClass(col.status)">{{ laneNote(col) }}</div>
          <draggable :list="columns[col.status]" :sort="false"
                     :group="{ name: 'eb-board', pull: canPullFrom(col.status), put: true }"
                     :move="validateMove" :disabled="saving || isMobileView"
                     ghost-class="eb-card--ghost" drag-class="eb-card--dragging"
                     :animation="180"
                     :force-fallback="true" :fallback-on-body="true" :fallback-tolerance="4"
                     class="eb-col-body" @change="onColumnChange(col.status, $event)"
                     @start="draggingFrom = col.status" @end="draggingFrom = null">
            <div v-for="e in columns[col.status]" :key="e.id"
                 class="eb-card"
                 :class="{
                   'eb-card--resolved': col.status === RESOLVED,
                   'eb-card--high': e.severity === 'high',
                   'eb-card--stale': isStale(e, col.status),
                   'eb-card--locked': !canPullFrom(col.status),
                   'eb-card--landed': flashes[e.id] === 'landed',
                   'eb-card--rejected': flashes[e.id] === 'rejected',
                 }"
                 @click="openEvent(e)">
              <div class="eb-card-top">
                <span class="eb-type-chip" :class="'eb-type-chip--' + e.entry_type">
                  <i class="fa" :class="entryTypeIcon(e.entry_type)"></i>
                  {{ entryTypeLabel(e.entry_type) }}
                </span>
                <span v-if="showSeverityChip(e.severity)" class="eb-sev-chip"
                      :class="['eb-sev-chip--' + severityKey(e.severity), { 'eb-sev-chip--dim': col.status === RESOLVED }]"
                      :title="'Severity: ' + severityLabel(e.severity)">
                  {{ severityLabel(e.severity) }}
                </span>
                <span class="eb-card-id ml-auto">#{{ e.id }}</span>
              </div>

              <div class="eb-card-what" :title="e.what_happened">
                {{ e.what_happened || '(no description)' }}
              </div>

              <div v-if="e.category || e.driver_id || e.vehicle_id" class="eb-card-meta">
                <span v-if="e.category" class="eb-cat-chip">{{ readableLabel(e.category) }}</span>
                <span v-if="e.driver_id" class="eb-meta"><i class="fa fa-user"></i>&nbsp;{{ e.driver_id }}</span>
                <span v-if="e.vehicle_id" class="eb-meta"><i class="fa fa-bus"></i>&nbsp;{{ e.vehicle_id }}</span>
              </div>

              <div class="eb-card-foot">
                <span v-if="isStale(e, col.status)" class="eb-flag eb-flag--stale">{{ ageInDays(e) }}d open</span>
                <span v-else-if="col.status === RESOLVED" class="eb-flag eb-flag--resolved">
                  {{ formatDate(e.updated_at || e.created_at) }}
                </span>
                <span v-else class="eb-flag">{{ formatDate(e.created_at) }}</span>
                <span class="ml-auto">
                  <span v-if="isMine(e)" class="eb-you-chip">You</span>
                  <span v-else-if="e.assigned_user_id" class="eb-assignee">{{ displayUserName(e.assigned_user_id) }}</span>
                  <span v-else class="eb-assignee eb-assignee--none">Unassigned</span>
                </span>
              </div>
            </div>
          </draggable>

          <!-- the lanes are capped so a three-year history cannot drag the
               board down. say so rather than imply the lane is complete. -->
          <div v-if="truncated[col.status]" class="eb-col-more">
            newest {{ col.status === RESOLVED ? RESOLVED_LANE_LIMIT : OPEN_LANE_LIMIT }} loaded —
            <router-link :to="logLinkFor(col.status)">see all in the Event Log</router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import draggable from 'vuedraggable'
import { list_ops_desk_events, transition_ops_desk_event_status } from '../networking/apis'
import { tenantContextDiffers, tenantDisplayName } from '../utils/tenant_context'
import ErmBoardHelp from './erm_board_help.vue'
import OdAddEntryModal from './od_add_entry_modal.vue'
import {
  OPEN_STATUSES, STATUS_RESOLVED,
  statusLabel, statusIcon, canTransition,
  typeLabel as opsTypeLabel, entryTypeIcon,
  severityLabel, severityKey, showSeverityChip,
} from '../utils/ops_labels'

// per-lane fetch caps. the open lanes are a working queue and should never
// come near theirs; resolved is an archive, so it shows only the recent tail.
// each lane is one status-filtered query, which the backend serves off the
// status gsi rather than draining the tenant's whole history.
const OPEN_LANE_LIMIT = 200
const RESOLVED_LANE_LIMIT = 200

// the Resolved lane is a recent-closures view rather than an archive, so it
// carries a window. the OPEN lanes deliberately do not: an event nobody has
// touched in four months is the most important card on this board, and a
// 7-day filter would hide exactly the work the stale flag exists to surface.
const RESOLVED_WINDOWS = [7, 30, 90]
const DEFAULT_RESOLVED_WINDOW = 7

// the fetch reaches back twice the widest window because the api can only
// bound on created_at — every gsi the list endpoint uses is keyed by it, and
// there is no index on updated_at. the lane is narrowed to "closed within N
// days" client-side, so the fetch has to cover events FILED long before they
// were closed. an event that took more than 180 days to resolve falls off the
// board; the lane's Event Log link is the way to those.
const RESOLVED_FETCH_DAYS = 180

// same vocabulary as the Event Log's chips, but as independent toggles
// rather than a one-of-N row: no "All" chip, an empty set means no
// narrowing, and picking two widens (Issue + Kudos shows both).
// 'incident' reads as 'Issue' — the stored value is frozen, the label is not.
const TYPE_FILTERS = [
  { value: 'incident', text: 'Issue' },
  { value: 'kudos', text: 'Kudos' },
  { value: 'note', text: 'Note' },
]
const SEVERITY_FILTERS = [
  { value: 'high', text: 'High' },
  { value: 'normal', text: 'Normal' },
  { value: 'low', text: 'Low' },
]

// an open event this old has stopped moving. mirrors nothing on the server —
// it is a board-only read of neglect, the way the coaching board reads
// overdue — so the threshold lives here.
const STALE_AFTER_DAYS = 7

// drag is off at or below this width. matches the @media (max-width: 768px)
// block at the foot of this file — the two must agree, or the card would
// still say "grab" while refusing to be dragged.
const MOBILE_MAX_WIDTH = 768

// how long a card keeps its landed / rejected pulse after a drop resolves
const FLASH_MS = 1500

// what a lane promises while a card is hovering over it. the workflow's own
// action labels read badly in the imperative ("Drop here to awaiting
// follow-up"), so the board words the promise itself.
const DROP_NOTES = {
  new: 'Drop here to mark new',
  in_progress: 'Drop here to start work',
  awaiting_followup: 'Drop here to await follow-up',
  resolved: 'Drop here to resolve',
}

export default {
  name: 'erm_board',
  components: { draggable, ErmBoardHelp, OdAddEntryModal },
  data() {
    return {
      loading: false,
      error: null,
      saving: false,
      events: [],

      // the swim lanes. draggable mutates these arrays on drop, so they are
      // plain data rebuilt from `events` — never computed.
      columns: { new: [], in_progress: [], awaiting_followup: [], resolved: [] },
      // per-lane: did the server have more than the cap?
      truncated: { new: false, in_progress: false, awaiting_followup: false, resolved: false },

      // free-text narrowing over the loaded events, composed with the
      // assignee scope. client-side on purpose: the board already holds every
      // card it can show, so the lanes narrow as you type instead of paying
      // four round trips per keystroke.
      search: '',

      // ≤600px folds the filter controls behind a toggle so a phone opens on
      // the lanes rather than on three rows of chips (Event Log precedent).
      filtersExpanded: false,

      // ≤768px turns dragging OFF entirely. force-fallback starts a drag as
      // soon as a touch moves, which on a phone is indistinguishable from
      // scrolling the lane — cards got picked up every time you tried to
      // scroll past them. Rather than tune a hold delay, touch widths drop
      // the drag surface and change status the way the Event Log does: tap
      // the card, use Move to on the event. Kept in sync by a resize
      // listener wired in mounted().
      isMobileView: false,

      // empty = every type / every severity; otherwise the selected values
      typeFilters: [],
      severityFilters: [],
      // '' = every driver / vehicle; otherwise the exact stored id
      driverFilter: '',
      vehicleFilter: '',
      TYPE_FILTERS,
      SEVERITY_FILTERS,

      // '' = everyone, MINE = the signed-in user, UNASSIGNED = no assignee,
      // any other string = that user's username
      assigneeFilter: '',
      MINE: '__mine__',
      UNASSIGNED: '__unassigned__',
      RESOLVED: STATUS_RESOLVED,
      // how far back the Resolved lane reaches, in days. switching it is a
      // client-side narrowing of what is already loaded, not a refetch.
      resolvedWindow: DEFAULT_RESOLVED_WINDOW,
      RESOLVED_WINDOWS,

      // template-visible copies of the per-lane caps, for the "loaded" note
      OPEN_LANE_LIMIT,
      RESOLVED_LANE_LIMIT,
      scopeInitialized: false,

      // the lane a card was picked up from, for the duration of the drag.
      // drives the lane highlighting: null means nothing is in flight.
      draggingFrom: null,
      // event id -> 'landed' | 'rejected'. a card that has just come to rest
      // pulses, so the eye can follow it to where it ended up.
      flashes: {},
      flashTimers: [],
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    tenant() {
      return this.$route.query.tenant || (this.user && this.user.tenant)
    },
    // admin deep-link context: name the tenant being rendered whenever it
    // differs from the navbar selection, matching the Event Log's rail.
    contextTenantName() {
      return tenantContextDiffers(this.$route, this.user) ? tenantDisplayName(this.user, this.tenant) : ''
    },
    // reopening a resolved event is admin-only server-side. this mirrors
    // event_manager._is_admin exactly — admin OR customer-admin, and NOT
    // tbn-admin, which transition_status rejects. (ops_desk.vue's detail view
    // offers its reopen button on the laxer canEditAdminFields, which includes
    // tbn-admin; that button 401s for them.)
    // FI-1820: the add-entry form offers an inline Bus SOS link on
    // breakdown / accident, but only where the tenant runs SOS.
    sosEnabled() {
      const f = this.user && this.user.features && this.user.features[this.tenant]
      return !!(f && f.bus_sos_enabled)
    },
    canReopen() {
      const role = this.user && this.user.role
      return role === 'admin' || role === 'customer-admin'
    },
    columnDefs() {
      const closed = `closed in the last ${this.resolvedWindow} days`
      // the reopen hint names a gesture, so it only appears where the gesture
      // exists — on touch widths the drag surface is off entirely
      const canDrag = this.canReopen && !this.isMobileView
      return [
        { status: 'new', note: 'filed, nobody has picked it up' },
        { status: 'in_progress', note: 'somebody is working it' },
        { status: 'awaiting_followup', note: 'waiting on someone else' },
        { status: 'resolved', note: canDrag ? `${closed} — drag back to reopen` : closed },
      ]
    },
    myOpenCount() {
      return this.events.filter(e => this.isMine(e) && OPEN_STATUSES.includes(e.status)).length
    },
    unassignedOpenCount() {
      return this.events.filter(e => !e.assigned_user_id && OPEN_STATUSES.includes(e.status)).length
    },
    scopedEvents() {
      return this.applyFilters(this.events)
    },
    matchCount() {
      return this.scopedEvents.length
    },
    driverOptions() {
      return this.distinctOptions('driver_id', 'driver', this.driverFilter, 'All drivers')
    },
    vehicleOptions() {
      return this.distinctOptions('vehicle_id', 'vehicle', this.vehicleFilter, 'All vehicles')
    },
    // the narrowing controls proper. the assignee scope and the Resolved
    // window are deliberately excluded: both always hold a value, so counting
    // them would mean the board is permanently "filtered".
    isFiltered() {
      return !!(this.search.trim() || this.typeFilters.length || this.severityFilters.length
        || this.driverFilter || this.vehicleFilter)
    },
    // badge on the mobile toggle: how many dimensions are narrowed while the
    // panel is folded away. everything counts, search included — it folds
    // away with the rest now, so nothing else on screen would show that a
    // search is trimming the lanes. Unlike isFiltered, the point here is to
    // say what is hidden behind the toggle, not whether the board is
    // filtered in the "nothing matches" sense.
    activeFilterCount() {
      let n = 0
      if (this.search.trim()) n++
      if (this.typeFilters.length) n++
      if (this.severityFilters.length) n++
      if (this.driverFilter) n++
      if (this.vehicleFilter) n++
      if (this.assigneeFilter) n++
      if (this.resolvedWindow !== DEFAULT_RESOLVED_WINDOW) n++
      return n
    },
    assigneeOptions() {
      const by = {}
      this.events.forEach(e => {
        if (!e.assigned_user_id) return
        const row = by[e.assigned_user_id] = by[e.assigned_user_id] || { open: 0 }
        if (OPEN_STATUSES.includes(e.status)) row.open += 1
      })
      const people = Object.entries(by)
        .map(([username, row]) => ({
          value: username,
          text: `${this.displayUserName(username)}${row.open ? ` (${row.open} open)` : ''}`,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
      return [{ value: null, text: 'By assignee…' }, ...people]
    },
    assigneeDropdown: {
      get() {
        return ['', this.MINE, this.UNASSIGNED].includes(this.assigneeFilter)
          ? null : this.assigneeFilter
      },
      set(value) {
        this.assigneeFilter = value || ''
      },
    },
  },
  watch: {
    // the navbar tenant switcher mutates user.tenant in place without
    // remounting the page (ops_desk.vue precedent). event ids are a dense
    // per-tenant counter, so they collide across tenants — drop every trace
    // of the old tenant's board before a drag can act on it.
    tenant() {
      this.events = []
      this.columns = { new: [], in_progress: [], awaiting_followup: [], resolved: [] }
      this.truncated = { new: false, in_progress: false, awaiting_followup: false, resolved: false }
      this.assigneeFilter = ''
      this.search = ''
      this.typeFilters = []
      this.severityFilters = []
      this.driverFilter = ''
      this.vehicleFilter = ''
      this.resolvedWindow = DEFAULT_RESOLVED_WINDOW
      this.scopeInitialized = false
      this.loadEvents()
    },
    assigneeFilter() {
      this.rebuildColumns()
    },
    search() {
      this.rebuildColumns()
    },
    typeFilters() {
      this.rebuildColumns()
    },
    severityFilters() {
      this.rebuildColumns()
    },
    driverFilter() {
      this.rebuildColumns()
    },
    vehicleFilter() {
      this.rebuildColumns()
    },
    // no refetch: the fetch already reaches past the widest window, so
    // switching is a client-side narrowing of what is loaded.
    resolvedWindow() {
      this.rebuildColumns()
    },
  },
  created() {
    this.loadEvents()
  },
  mounted() {
    this._onResize = () => { this.isMobileView = window.innerWidth <= MOBILE_MAX_WIDTH }
    this._onResize()
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    this.flashTimers.forEach(clearTimeout)
    if (this._onResize) window.removeEventListener('resize', this._onResize)
  },
  methods: {
    statusLabel, statusIcon, entryTypeIcon, severityLabel, severityKey, showSeverityChip,
    entryTypeLabel(v) { return v ? opsTypeLabel(v) : '-' },
    readableLabel(v) {
      if (!v) return '-'
      return String(v).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },
    // same display rule as the Event Log: drop the domain on @ addresses.
    displayUserName(username) {
      if (!username) return ''
      const at = username.indexOf('@')
      return at > 0 ? username.slice(0, at) : username
    },
    formatDate(iso) {
      if (!iso) return '—'
      const m = moment(iso)
      // events get revisited months later, so only this year's may drop it
      return m.year() === moment().year() ? m.format('MMM D') : m.format('MMM D, YYYY')
    },
    ageInDays(e) {
      if (!e.created_at) return 0
      return Math.max(0, moment().diff(moment(e.created_at), 'days'))
    },
    isStale(e, status) {
      return OPEN_STATUSES.includes(status) && this.ageInDays(e) >= STALE_AFTER_DAYS
    },
    isMine(e) {
      const me = this.user && this.user.username
      return !!(me && e.assigned_user_id === me)
    },
    apiErrorMessage(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback
    },

    // deliberately the same fields the Event Log's server-side search covers
    // (event_manager._matches_search), so the same words find the same events
    // on both surfaces. the id matches EXACTLY, never by substring: ids are a
    // dense per-tenant counter, so a bare "12" would otherwise drag in #112,
    // #120-#129, #212... charter_ref stays out, matching the Event Log.
    matchesSearch(e, needle) {
      if (e.id !== undefined && e.id !== null && String(e.id).toLowerCase() === needle) return true
      const fields = ['driver_id', 'vehicle_id', 'what_happened',
                      'action_items', 'follow_up_resolution', 'coaching_note']
      if (fields.some(f => e[f] != null && String(e[f]).toLowerCase().includes(needle))) return true
      // the category both as stored ("hotel_issue") and as a human types it
      const category = e.category ? String(e.category).toLowerCase() : ''
      if (category && (category.includes(needle) || category.replace(/_/g, ' ').includes(needle))) return true
      // legacy sheet rows keep their original wording in extra_fields, so a
      // "hotel" search would otherwise miss every imported hotel event
      const extras = e.extra_fields
      if (extras && typeof extras === 'object') {
        return Object.keys(extras).some(k =>
          extras[k] != null && String(extras[k]).toLowerCase().includes(needle))
      }
      return false
    },
    logLinkFor(status) {
      return { name: 'ops_desk', query: { tenant: this.tenant, status } }
    },

    // one filter pipeline for every narrowing control. `skip` names a
    // dimension to leave out, which is how the driver and vehicle dropdowns
    // ask "what would be left if my own filter were not applied?" — without
    // it, choosing a driver would immediately collapse that dropdown to the
    // single value just chosen.
    applyFilters(rows, skip = '') {
      if (skip !== 'assignee') {
        if (this.assigneeFilter === this.UNASSIGNED) {
          rows = rows.filter(e => !e.assigned_user_id)
        } else if (this.assigneeFilter) {
          const target = this.assigneeFilter === this.MINE
            ? (this.user && this.user.username) : this.assigneeFilter
          rows = rows.filter(e => e.assigned_user_id === target)
        }
      }
      // the Resolved lane is bounded by when the event was CLOSED, not when it
      // was filed — a month-old complaint settled yesterday is a recent
      // closure. updated_at is the close stamp (update_event_status writes
      // it); rows that never transitioned through the ui fall back to filing.
      const closedAfter = moment().subtract(this.resolvedWindow, 'days')
      rows = rows.filter(e => e.status !== STATUS_RESOLVED
        || moment(e.updated_at || e.created_at).isAfter(closedAfter))
      // an empty chip set is not a filter — it means every value is welcome
      if (skip !== 'type' && this.typeFilters.length) {
        rows = rows.filter(e => this.typeFilters.includes(e.entry_type))
      }
      if (skip !== 'severity' && this.severityFilters.length) {
        // severity postdates the first events, and an absent one renders as
        // Normal on the card — so it has to answer to the Normal chip too
        // (the api's own normal filter does this with a not_exists check).
        rows = rows.filter(e => this.severityFilters.includes(e.severity || 'normal'))
      }
      if (skip !== 'driver' && this.driverFilter) {
        rows = rows.filter(e => e.driver_id === this.driverFilter)
      }
      if (skip !== 'vehicle' && this.vehicleFilter) {
        rows = rows.filter(e => e.vehicle_id === this.vehicleFilter)
      }
      const needle = this.search.trim().toLowerCase()
      if (skip !== 'search' && needle) rows = rows.filter(e => this.matchesSearch(e, needle))
      return rows
    },

    // distinct values for one field over the rows that survive every OTHER
    // filter, so the list only ever offers choices that return something. the
    // current selection is kept even when the rest of the board filters it
    // away, or the select would blank out while still filtering.
    distinctOptions(field, dimension, selected, placeholder) {
      const seen = new Set(
        this.applyFilters(this.events, dimension)
          .map(e => (e[field] || '').trim())
          .filter(Boolean),
      )
      if (selected) seen.add(selected)
      const values = [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      return [{ value: '', text: placeholder }, ...values.map(v => ({ value: v, text: v }))]
    },
    // the chip rows are independent toggles, not a one-of-N choice: an empty
    // set means "every value", so there is no "All" chip to press — you
    // deselect back to everything. replaces the array rather than mutating so
    // the watcher fires on the reference change.
    toggleFilter(name, value) {
      const list = this[name]
      this[name] = list.includes(value)
        ? list.filter(v => v !== value)
        : [...list, value]
    },

    // clears the narrowing controls only — the assignee scope stays where the
    // viewer put it, and the Resolved window is a lane setting, not a filter.
    clearFilters() {
      this.search = ''
      this.typeFilters = []
      this.severityFilters = []
      this.driverFilter = ''
      this.vehicleFilter = ''
    },

    // one status-filtered query per lane, in parallel. keeps every lane off
    // the tenant date gsi (which would page newest-first across all statuses
    // and leave the older open work invisible below the cap) and lets the
    // resolved archive carry a much smaller cap than the working lanes.
    async loadEvents() {
      this.loading = true
      this.error = null
      try {
        const lanes = this.columnDefs.map(c => c.status)
        const responses = await Promise.all(lanes.map(status => list_ops_desk_events(this.tenant, {
          status,
          limit: status === STATUS_RESOLVED ? RESOLVED_LANE_LIMIT : OPEN_LANE_LIMIT,
          // resolved only: bound the fetch so a three-year archive is never
          // drained. the open lanes stay unbounded — old open work is the
          // point of the board. `from` filters created_at (the gsi sort key),
          // which is why it reaches back further than any window offered.
          from: status === STATUS_RESOLVED
            ? moment().subtract(RESOLVED_FETCH_DAYS, 'days').format('YYYY-MM-DD')
            : undefined,
          // the board renders no filter chips, so skip the full-history
          // aggregate drain the list endpoint does for them
          skip_counts: 1,
        })))
        const events = []
        const truncated = {}
        responses.forEach((resp, i) => {
          const { value: { data = {} } = {} } = resp || {}
          const items = data.items || []
          events.push(...items)
          truncated[lanes[i]] = !!data.next_token
        })
        this.events = events
        this.truncated = truncated
        if (!this.scopeInitialized) {
          this.assigneeFilter = this.myOpenCount > 0 ? this.MINE : ''
          this.scopeInitialized = true
        }
        this.rebuildColumns()
      } catch (e) {
        this.error = this.apiErrorMessage(e, 'Failed to load the event board.')
      } finally {
        this.loading = false
      }
    },
    reload() {
      return this.loadEvents()
    },

    rebuildColumns() {
      const cols = { new: [], in_progress: [], awaiting_followup: [], resolved: [] }
      this.scopedEvents.forEach(e => {
        if (cols[e.status]) cols[e.status].push(e)
      })
      // open lanes: the loud and the neglected first (high severity, then
      // oldest — an event nobody has touched in a month outranks this
      // morning's). resolved: most recently closed first.
      const sevRank = e => (e.severity === 'high' ? 0 : e.severity === 'low' ? 2 : 1)
      const openSort = (a, b) => sevRank(a) - sevRank(b)
        || String(a.created_at || '').localeCompare(String(b.created_at || ''))
      cols.new.sort(openSort)
      cols.in_progress.sort(openSort)
      cols.awaiting_followup.sort(openSort)
      cols.resolved.sort((a, b) => String(b.updated_at || b.created_at || '')
        .localeCompare(String(a.updated_at || a.created_at || '')))
      this.columns = cols
    },

    // resolved cards only leave the lane for an admin (the reopen path);
    // everyone else's resolved cards are read-only on the board.
    canPullFrom(status) {
      return status !== STATUS_RESOLVED || this.canReopen
    },

    // what this lane is to the card currently in flight: where it came from,
    // somewhere it may land, or somewhere the workflow will not let it. drives
    // the lane highlighting AND the note text, so the answer is computed once.
    dropState(status) {
      if (!this.draggingFrom) return ''
      if (status === this.draggingFrom) return 'source'
      return canTransition(this.draggingFrom, status, this.canReopen) ? 'target' : 'blocked'
    },
    dropStateClass(status) {
      const state = this.dropState(status)
      return state ? 'eb-col--drop-' + state : ''
    },
    noteStateClass(status) {
      const state = this.dropState(status)
      return (state === 'target' || state === 'blocked') ? 'eb-col-note--' + state : ''
    },
    laneNote(col) {
      const state = this.dropState(col.status)
      if (state === 'target') {
        return (this.draggingFrom === STATUS_RESOLVED && col.status === 'in_progress')
          ? 'Drop here to reopen' : DROP_NOTES[col.status]
      }
      if (state === 'blocked') return 'Not a move the workflow allows'
      return col.note
    },
    // a drop is over in a blink and the card is one of many — pulse it where
    // it came to rest so the outcome is visible without hunting for the id.
    flashCard(id, kind) {
      this.$set(this.flashes, id, kind)
      const timer = setTimeout(() => {
        this.$delete(this.flashes, id)
        this.flashTimers = this.flashTimers.filter(t => t !== timer)
      }, FLASH_MS)
      this.flashTimers.push(timer)
    },

    // sortablejs move hook: refuse anything the backend's transition table
    // would reject, so an illegal drop never round-trips into an error toast.
    validateMove(evt) {
      const event = evt.draggedContext.element
      const toEl = evt.to.closest('.eb-col')
      if (!toEl) return false
      const target = this.columnDefs.find(c => toEl.classList.contains('eb-col--' + c.status))
      if (!target) return false
      return canTransition(event.status, target.status, this.canReopen)
    },

    async onColumnChange(toStatus, evt) {
      if (!evt.added) return
      const event = evt.added.element
      this.saving = true
      try {
        const { value: { data = {} } = {} } = await transition_ops_desk_event_status(
          this.tenant, event.id, toStatus,
        ) || {}
        // apply the server's row in place rather than refetching all four
        // lanes; it carries the new status plus the updated_at the resolved
        // lane sorts on.
        const i = this.events.findIndex(e => e.id === event.id)
        if (i !== -1) this.$set(this.events, i, { ...this.events[i], ...data, status: toStatus })
        this.flashCard(event.id, 'landed')
        this.$bvToast.toast(
          `Event #${event.id} moved to ${statusLabel(toStatus)}.`,
          { title: 'Event Response Manager', variant: 'info', autoHideDelay: 3000 },
        )
      } catch (e) {
        this.$bvToast.toast(
          this.apiErrorMessage(e, `Could not move event #${event.id} to ${statusLabel(toStatus)}.`),
          { title: 'Event Response Manager', variant: 'danger', autoHideDelay: 6000 },
        )
        // the drag moved the card between the lane arrays but never touched
        // the event's own status, so the rebuild below snaps it back — the
        // pulse says which card just bounced.
        this.flashCard(event.id, 'rejected')
      } finally {
        this.saving = false
        this.rebuildColumns()
      }
    },

    // from=board is what sends the detail's back arrow here rather than to the
    // Event Log (ops_desk.vue backTarget). push, not replace: the browser's
    // own back button should walk back to the board too.
    openEvent(event) {
      this.openEventById(event.id)
    },
    openEventById(id) {
      this.$router.push({
        name: 'ops_desk',
        query: { tenant: this.tenant, event_id: id, from: 'board' },
      }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.eb-root {
  max-width: none;
  padding: 12px 20px 32px 20px;
  text-align: left;
  font-size: 14px;
}
/* the rail bleeds into the page gutters so its TITLE TEXT lines up with the
   lanes below it rather than sitting 19px further in. -19px is exactly the
   rail's own inset (3px accent border + 16px padding), so pulling the box out
   by that much lands the text back on the root's 20px gutter; 1px of the
   root's 12px top / 20px side padding survives on each bled side. This is the
   Event Log's .od-rail--flush treatment, applied here rather than in
   FiPageRail so no other page shifts — FiPageRail's own comment describes
   this bleed but no page implements it, so every rail page has the same 19px
   step. Mobile keeps the stock chrome, matching the Event Log. */
@media (min-width: 601px) {
  .eb-root ::v-deep .fi-pr-rail { margin: -11px -19px 14px -19px; }
}
.eb-stale { opacity: 0.6; }
.eb-scope-row { display: flex; align-items: center; gap: 6px; margin: 14px 0 12px; flex-wrap: wrap; }
.eb-mobile-only { display: none; }
.eb-filter-toggle {
  align-items: center; gap: 7px;
  background: transparent;
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 7px 14px; margin: 14px 0 10px;
  font-size: 13.5px; font-weight: 600;
  color: var(--fi-text, #374151);
  cursor: pointer; transition: background .12s, border-color .12s;
}
.eb-filter-toggle:hover { background: var(--fi-surface-sunken, #eef4fb); }
.eb-filter-toggle:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }
.eb-filter-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
  font-size: 11px; font-weight: 700; line-height: 1;
}
.eb-scope-select { max-width: 220px; font-size: 0.85em; }
/* driver / vehicle sit inline with the chip groups, so they stay narrow —
   ids and surnames are short, and the row reads as one band of controls
   rather than two wide fields. the value ellipsises rather than widening. */
.eb-filter-select {
  max-width: 132px; font-size: 0.85em;
  text-overflow: ellipsis;
}

/* search pill. paints no light background of its own, same reason the cards
   don't — the tokens carry it into midnight. */
.eb-search {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 10px;
  background: transparent;
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  min-width: 200px;
}
.eb-search:focus-within { border-color: var(--fi-ops-accent, #3a6e99); }
.eb-search-icon { font-size: 11px; color: var(--fi-text-muted, #8a94a1); }
.eb-search-input {
  flex: 1 1 auto; min-width: 0;
  background: transparent;
  border: 0; outline: none;
  font-size: 12.5px;
  color: var(--fi-text, #2c2c2a);
}
.eb-search-input::placeholder { color: var(--fi-text-faint, #9aa4b1); }
.eb-search-clear {
  background: none; border: 0; padding: 0;
  font-size: 11px; line-height: 1;
  color: var(--fi-text-muted, #8a94a1);
  cursor: pointer;
}
.eb-search-clear:hover { color: var(--fi-ops-accent, #3a6e99); }
.eb-no-matches {
  font-size: 12.5px;
  color: var(--fi-text-muted, #6b7280);
  margin: 0 0 10px;
}
.eb-link-btn {
  background: none; border: 0; padding: 0;
  font-size: inherit;
  color: var(--fi-ops-accent, #3a6e99);
  text-decoration: underline;
  cursor: pointer;
}
/* the filter row: what the cards are, under the row about whose they are */
.eb-filter-row {
  display: flex; align-items: center; flex-wrap: wrap;
  gap: 6px 18px;
  margin: 0 0 12px;
}
.eb-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 3px 9px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
.eb-chip:hover { background: var(--fi-surface-sunken, #eef4fb); }
/* the window pills read as one control with the lane they narrow */
.eb-group { display: inline-flex; align-items: center; gap: 4px; }
/* the Resolved window governs one lane rather than the whole board, so it
   sits away from the filters that narrow every lane */
.eb-group--end { margin-left: auto; }
.eb-group-label {
  font-size: 10.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.4px;
  color: var(--fi-text-muted, #6b7280);
}
.eb-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}

/* the board: four equal lanes, horizontal scroll when squeezed */
.eb-board { display: flex; gap: 12px; align-items: flex-start; overflow-x: auto; padding-bottom: 8px; }
.eb-col {
  flex: 1 1 0; min-width: 250px;
  background: var(--fi-surface-sunken, #f4f6f8);
  border: 1px solid var(--fi-line, #dee2e6);
  border-radius: 8px;
  padding: 8px;
}
.eb-col-head { display: flex; align-items: center; justify-content: space-between; padding: 2px 2px 0; }
.eb-col-count { font-size: 12px; font-weight: 700; color: var(--fi-text-muted, #6b7280); font-variant-numeric: tabular-nums; }
.eb-col-note { font-size: 11px; color: var(--fi-text-muted, #8a94a1); padding: 2px 2px 6px; }
/* the lane must stay a drop target when empty */
.eb-col-body { min-height: 60px; display: flex; flex-direction: column; gap: 6px; }
.eb-col-more { font-size: 11px; color: var(--fi-text-muted, #8a94a1); padding: 8px 2px 0; }
/* lane top accents follow the status chip families */
.eb-col--new { box-shadow: inset 0 3px 0 0 #9aa4b1; }
.eb-col--in_progress { box-shadow: inset 0 3px 0 0 #8db8f2; }
.eb-col--awaiting_followup { box-shadow: inset 0 3px 0 0 #f0d97a; }
.eb-col--resolved { box-shadow: inset 0 3px 0 0 #7bcf99; }

/* the card paints no background of its own: the lane's surface shows through,
   so the board follows the theme into midnight instead of stamping white
   cards onto a dark canvas. the border and the severity edge carry the
   card's shape. */
.eb-card {
  background: transparent;
  border: 1px solid var(--fi-line-strong, #dfe4ea);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  cursor: grab;
  transition: border-color .12s, box-shadow .12s;
}
.eb-card:hover { border-color: var(--fi-ops-accent, #3a6e99); }
.eb-card--resolved { opacity: 0.82; }
.eb-card--locked { cursor: pointer; }
.eb-card--stale { box-shadow: inset 3px 0 0 0 #e8912d; }
.eb-card--high { box-shadow: inset 3px 0 0 0 #d62f2f; }

/* ---- the drag, made legible -----------------------------------------
   a drop is over in a blink, so the board narrates it in four places: the
   slot the card left goes hollow, the card in flight lifts off the board,
   the lanes sort themselves into "you may land here" and "not from where
   you started", and the card pulses once where it comes to rest. */

/* the slot left behind: a hole in the lane, not a faded copy of the card.
   the content keeps its space (visibility, not display) so nothing below
   jumps while the card is in the air. */
/* every wash below is a translucent accent rather than a mixed light colour,
   so it reads the same over the light canvas and over midnight's. */
.eb-card--ghost {
  opacity: 1;
  background: rgba(58, 110, 153, 0.12);
  border: 2px dashed var(--fi-ops-accent, #3a6e99);
  box-shadow: none;
}
.eb-card--ghost > * { visibility: hidden; }

/* the card under the cursor. force-fallback makes this our own clone rather
   than the browser's flat drag-image snapshot, so it can actually be styled
   — but sortablejs owns its inline transform/opacity/position, so the lift
   is expressed in border and shadow only. */
/* the one card that DOES need to paint: force-fallback floats this clone
   over the whole page, so a transparent one would show the page through it. */
.eb-card--dragging {
  cursor: grabbing;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-ops-accent, #3a6e99);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.30);
}

/* lanes answer "can this land here?" for as long as a card is in the air */
.eb-col--drop-target {
  background: rgba(58, 110, 153, 0.10);
  outline: 2px solid var(--fi-ops-accent, #3a6e99);
  outline-offset: -1px;
}
.eb-col--drop-target .eb-col-body {
  box-shadow: inset 0 0 0 1px rgba(58, 110, 153, 0.35);
  border-radius: 6px;
}
.eb-col--drop-blocked { opacity: 0.4; }
/* the source lane needs no wash: the hollow slot the card left already
   marks where it came from. */
.eb-col-note--target { color: var(--fi-ops-accent, #3a6e99); font-weight: 700; }
.eb-col-note--blocked { font-style: italic; }

/* the landing. a ring that blooms and fades, green where the move stuck and
   red where the server sent the card back. */
@keyframes eb-pulse-landed {
  0%   { box-shadow: 0 0 0 4px rgba(22, 101, 52, 0.45); border-color: #166534; }
  100% { box-shadow: 0 0 0 4px rgba(22, 101, 52, 0);    border-color: var(--fi-line-strong, #dfe4ea); }
}
@keyframes eb-pulse-rejected {
  0%   { box-shadow: 0 0 0 4px rgba(185, 28, 28, 0.45); border-color: #b91c1c; }
  100% { box-shadow: 0 0 0 4px rgba(185, 28, 28, 0);    border-color: var(--fi-line-strong, #dfe4ea); }
}
.eb-card--landed { animation: eb-pulse-landed 1.5s ease-out 1; }
.eb-card--rejected { animation: eb-pulse-rejected 1.5s ease-out 1; }

/* the pulses and the lane fades are pure decoration; anyone who has asked
   for less motion gets the colour without the animation. */
@media (prefers-reduced-motion: reduce) {
  .eb-card--landed, .eb-card--rejected { animation-duration: 1ms; }
  .eb-card--landed { box-shadow: 0 0 0 3px rgba(22, 101, 52, 0.45); }
  .eb-card--rejected { box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.45); }
}
.eb-card-top { display: flex; align-items: center; gap: 4px; }
.eb-card-id { font-size: 11px; color: var(--fi-text-muted, #8a94a1); font-variant-numeric: tabular-nums; }
.eb-card-what {
  margin-top: 4px; color: var(--fi-text, #374151); line-height: 1.35;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
  overflow: hidden;
}
.eb-card-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 8px; margin-top: 6px; }
.eb-meta { font-size: 11px; color: var(--fi-text-muted, #6b7280); white-space: nowrap; }
.eb-card-foot { display: flex; align-items: center; gap: 6px; margin-top: 6px; }
.eb-flag { font-size: 11px; color: var(--fi-text-muted, #6b7280); white-space: nowrap; }
.eb-flag--stale { color: #b45309; font-weight: 600; }
.eb-flag--resolved { color: #166534; }
.eb-assignee { font-size: 11px; color: var(--fi-text-muted, #6b7280); }
.eb-assignee--none { font-style: italic; color: var(--fi-text-muted, #9aa4b1); }
.eb-you-chip {
  display: inline-block; padding: 1px 8px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
}

/* chip families shared with the Event Log (od- copies are scoped there) */
.eb-status-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px;
  font-size: 11px; line-height: 1.4; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
}
.eb-status-chip .fa { font-size: 10px; line-height: 1; }
.eb-status-chip--new               { background: #f1f3f5; color: #4b5563; border-color: #d6dde6; }
.eb-status-chip--in_progress       { background: #e7f1ff; color: #0b3d91; border-color: #bcd8fb; }
.eb-status-chip--awaiting_followup { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.eb-status-chip--resolved          { background: #e7f8ed; color: #166534; border-color: #b5e3c5; }

.eb-type-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 1px 7px;
  font-size: 10.5px; line-height: 1.5; font-weight: 600;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
}
.eb-type-chip .fa { font-size: 9.5px; }
.eb-type-chip--incident { background: #fee4d1; color: #b85a1e; border-color: #f0c7a4; }
.eb-type-chip--kudos    { background: #ddf3e3; color: #146230; border-color: #b7e2c4; }
.eb-type-chip--note     { background: #eef1f5; color: #4b5563; border-color: #d6dde6; }

.eb-sev-chip {
  display: inline-flex; align-items: center;
  padding: 1px 7px;
  font-size: 10.5px; line-height: 1.5; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 999px; border: 1px solid;
}
.eb-sev-chip--high { background: #fdeaea; color: #b91c1c; border-color: #f3c2c2; }
.eb-sev-chip--low  { background: #f4f6f8; color: #8a94a1; border-color: #e2e7ec; }
.eb-sev-chip--dim  { opacity: 0.6; }

.eb-cat-chip {
  display: inline-flex; align-items: center;
  padding: 1px 7px;
  font-size: 10.5px; line-height: 1.5;
  border-radius: 4px;
  background: #eef1f5; color: #4b5563; border: 1px solid #e2e7ec;
}

@media (max-width: 768px) {
  .eb-root { padding: 12px 8px 32px 8px; }
  /* dragging is disabled at this width (see isMobileView), so the card must
     not advertise a grab it will not honour — it is a tap target that opens
     the event, where the Move to buttons live. */
  .eb-card { cursor: pointer; }
  /* the search takes the full width of its own row rather than being
     squeezed to nothing beside the scope chips */
  .eb-search { min-width: 100%; margin-left: 0; }

}

@media (max-width: 600px) {
  /* mobile-only affordances turn on here */
  .eb-mobile-only { display: inline-flex; }
  /* FiPageRail wraps its actions onto a full-width second row on a phone,
     which is right for pages with worded buttons. all three of ours are
     single icons, so they belong beside the title and that row is wasted
     height. un-stretching the actions is not enough on its own — the rail
     is still flex-wrap:wrap — so the wrap is switched off too. the rail
     element is the child's ROOT, so it carries this component's scope id.

     these go through .eb-root deliberately. scoped css stamps an attribute
     on the RAIL's own selectors too, so `.fi-pr-rail[data-v-erm]` and the
     rail's `.fi-pr-rail[data-v-rail]` weigh exactly the same and source
     order decides — and the child's stylesheet is injected last, so it won
     and the header kept wrapping. Going through the root adds a third
     component to the specificity and settles it outright. */
  .eb-root ::v-deep .fi-pr-rail { flex-wrap: nowrap; }
  .eb-root ::v-deep .fi-pr-actions { width: auto; flex-shrink: 0; }
  /* with no wrap to fall back on, a long title gives way (clips) rather than
     pushing the icons off the edge. no ellipsis: .fi-pr-title is a flex
     container, so its text is an anonymous flex item and text-overflow
     would not apply to it. */
  .eb-root ::v-deep .fi-pr-title { min-width: 0; overflow: hidden; }
  /* collapsed: every control folds away, search included, so a phone opens
     straight onto the lanes and spends no rows on chrome. the toggle above
     flips it back, and its badge counts what is hidden. */
  .eb-filters-collapsed .eb-scope-row,
  .eb-filters-collapsed .eb-filter-row { display: none; }
  /* each group scrolls on its own line rather than wrapping into a wall */
  .eb-filter-row { flex-direction: column; align-items: flex-start; gap: 8px; }
  .eb-group { flex-wrap: wrap; }
  /* stacked, there is no "far right" to push to — the auto margin would
     shove the Resolved group off to the edge on its own line */
  .eb-group--end { margin-left: 0; }

  /* one lane per screen instead of a ~1000px scrubbable strip. the lane
     takes 86% so the next one's edge stays visible — that sliver is the
     only thing telling you there are three more — and the scroll snaps so
     a swipe lands square on a lane rather than halfway between two. only on
     phones: a tablet has room for the overview and should keep it. */
  .eb-board {
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
  }
  .eb-col {
    flex: 0 0 86%;
    min-width: 0;
    scroll-snap-align: start;
  }
}
</style>
